import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = "/api";

function Welcome() {
  const [callStarted, setCallStarted] = useState(false);
  const [greetingUrl, setGreetingUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [message, setMessage] = useState("");
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const navigate = useNavigate();

  const initiateCall = async () => {
    try {
      const res = await axios.post(`${API_BASE}/initiate-call-public`);
      setGreetingUrl(res.data.greeting_url);
      setMessage(res.data.message);
      setCallStarted(true);
    } catch (error) {
      console.error("Error initiating call:", error);
    }
  };

  const startRecording = async () => {
    try {
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        alert("Video recording is not supported in this browser.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      videoRef.current.srcObject = stream;

      // Detect supported mime type
      let mimeType = '';
      let fileExtension = 'webm';
      
      // Try different formats in order of preference
      const formats = [
        { mime: 'video/webm;codecs=vp9', ext: 'webm' },
        { mime: 'video/webm;codecs=vp8', ext: 'webm' },
        { mime: 'video/webm', ext: 'webm' },
        { mime: 'video/mp4;codecs=h264', ext: 'mp4' },
        { mime: 'video/mp4', ext: 'mp4' }
      ];

      for (const format of formats) {
        if (MediaRecorder.isTypeSupported(format.mime)) {
          mimeType = format.mime;
          fileExtension = format.ext;
          break;
        }
      }

      // If no format is supported, try without specifying mimeType
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      
      // If we didn't find a supported format, use the browser's default
      if (!mimeType) {
        fileExtension = 'webm'; // Default fallback
      }

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
        const formData = new FormData();
        formData.append("video", blob, `reply.${fileExtension}`);

        console.log('Uploading video:', {
          size: blob.size,
          type: blob.type,
          filename: `reply.${fileExtension}`
        });

        try {
          await axios.post(`${API_BASE}/upload-reply-public`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          
          // Redirect to thank you page
          navigate('/thank-you');
        } catch (error) {
          console.error("Error uploading reply:", error);
          console.error("Full error:", error.response?.data);
          
          let errorMsg = "Unknown error";
          if (error.response?.data?.errors) {
            errorMsg = Object.values(error.response.data.errors).flat().join(', ');
          } else if (error.response?.data?.message) {
            errorMsg = error.response.data.message;
          } else {
            errorMsg = error.message;
          }
          
          alert("Error uploading your message: " + errorMsg);
        }
        
        chunksRef.current = [];
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Could not access camera/microphone: " + error.message);
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    
    // Stop all tracks (camera and microphone)
    const stream = videoRef.current.srcObject;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    
    setRecording(false);
  };

  return (
    <div className="welcome-page">
      {!callStarted ? (
        <div className="welcome-hero">
          <h1>Welcome to Hermes Echo</h1>
          <p className="welcome-subtitle">Connect with our sales team through video</p>
          <button onClick={initiateCall} className="cta-button">
            📞 Video Call the Sales Person
          </button>
        </div>
      ) : (
        <div className="call-interface">
          {greetingUrl ? (
            <>
              <h2>Message from Sales</h2>
              <video src={greetingUrl} controls autoPlay width="640" className="greeting-video" />
            </>
          ) : (
            <div className="no-greeting">
              <h2>Sales Team Message</h2>
              <p style={{color: "orange"}}>{message}</p>
            </div>
          )}
          
          <div className="response-section">
            <h3>Leave Your Response</h3>
            {!recording && (
              <button onClick={startRecording} className="record-button">
                🎥 Start Recording
              </button>
            )}
            {recording && (
              <button onClick={stopRecording} className="stop-button">
                ⏹ Stop & Send
              </button>
            )}
            <video 
              ref={videoRef} 
              autoPlay 
              muted 
              width="640" 
              className="preview-video"
              style={{display: recording ? "block" : "none", margin: "1rem auto"}}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Welcome;
